/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { css, Global } from '@emotion/react'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)
import { DatePicker } from 'antd'
import { CalendarOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import type { CalendarGame } from './CalendarGameCard'
import type { Venue } from '../hooks/useSchedulerLocations'
import type { DivisionItem } from '../hooks/useSchedulerDivisions'
import type { TimeSlot } from '../hooks/useSchedulerTimeSlots'
import {
  type HourSlot,
  getTimeRangesForFacilityAndDay,
  splitIntoHourSlots,
  buildEditDiff,
} from '../utils/gameEditHelpers'

interface TeamOption {
  id: string
  name: string
}

interface EditFormState {
  venueId: string
  facilityId: string
  homeTeamId: string
  awayTeamId: string
  startsAt: string
  endsAt: string
}

export interface CalendarGameEditPopoverProps {
  game: CalendarGame
  venues: Venue[]
  divisions: DivisionItem[]
  timeSlots: TimeSlot[]
  onClose: () => void
  onSave: (gameId: string, data: Record<string, unknown>) => Promise<boolean>
  onMoveToDate?: (gameId: string) => void
  readOnly?: boolean
}

const CalendarGameEditPopover: React.FC<CalendarGameEditPopoverProps> = ({
  game,
  venues,
  divisions,
  timeSlots,
  onClose,
  onSave,
  onMoveToDate,
  readOnly = false,
}) => {
  const [editForm, setEditForm] = useState<EditFormState>({
    venueId: game.locationId,
    facilityId: game.surfaceId,
    homeTeamId: game.homeTeamId,
    awayTeamId: game.awayTeamId,
    startsAt: game.startsAt,
    endsAt: game.endsAt,
  })

  // Store original values for diffing on save
  const originalRef = React.useRef({
    locationId: game.locationId,
    surfaceId: game.surfaceId,
    homeTeamId: game.homeTeamId,
    awayTeamId: game.awayTeamId,
    startsAt: game.startsAt,
    endsAt: game.endsAt,
  })

  // Update ref if game prop changes (e.g. after undo)
  useEffect(() => {
    originalRef.current = {
      locationId: game.locationId,
      surfaceId: game.surfaceId,
      homeTeamId: game.homeTeamId,
      awayTeamId: game.awayTeamId,
      startsAt: game.startsAt,
      endsAt: game.endsAt,
    }
  }, [game])

  // Teams filtered by division
  const divisionTeams = useMemo(() => {
    for (const div of divisions) {
      if (div.id === game.divisionId && div.teams) {
        return div.teams.map((t: { id: string; name: string }) => ({
          id: t.id,
          name: t.name,
        }))
      }
    }
    return [] as TeamOption[]
  }, [divisions, game.divisionId])

  // Composite venue::facility value for the grouped dropdown
  const venueCompositeValue = useMemo(() => {
    if (editForm.venueId && editForm.facilityId) {
      return `${editForm.venueId}::${editForm.facilityId}`
    }
    return ''
  }, [editForm.venueId, editForm.facilityId])

  // Date derivations
  const selectedDate = useMemo(
    () => (editForm.startsAt ? editForm.startsAt.slice(0, 10) : ''),
    [editForm.startsAt]
  )

  const availableTimeRanges = useMemo(() => {
    if (!editForm.facilityId || !selectedDate) {
      return []
    }
    const dow = dayjs.utc(selectedDate).day()
    return getTimeRangesForFacilityAndDay(timeSlots, editForm.facilityId, dow)
  }, [timeSlots, editForm.facilityId, selectedDate])

  const availableHourSlots = useMemo(
    () => splitIntoHourSlots(availableTimeRanges),
    [availableTimeRanges]
  )

  const selectedTime = useMemo(() => {
    if (!editForm.startsAt || editForm.startsAt.length < 16) {
      return ''
    }
    return editForm.startsAt.slice(11, 16)
  }, [editForm.startsAt])

  // Dropdown options: availableHourSlots + the game's current slot if not already listed
  const timeSlotOptions = useMemo(() => {
    if (
      !selectedTime ||
      availableHourSlots.some((s) => s.start === selectedTime)
    ) {
      return availableHourSlots
    }
    const endTime =
      editForm.endsAt?.length >= 16
        ? editForm.endsAt.slice(11, 16)
        : selectedTime
    const currentSlot: HourSlot = { start: selectedTime, end: endTime }
    return [...availableHourSlots, currentSlot].sort((a, b) =>
      a.start.localeCompare(b.start)
    )
  }, [availableHourSlots, selectedTime, editForm.endsAt])

  // Build diff and call onSave
  const saveChanges = useCallback(async () => {
    const data = buildEditDiff(editForm, originalRef.current)
    if (data) {
      await onSave(game.id, data)
    }
  }, [editForm, game.id, onSave])

  // Resolve venue display name for read-only mode
  const venueDisplayName = useMemo(() => {
    for (const v of venues) {
      for (const f of v.facilities ?? []) {
        if (f.id === game.surfaceId) {
          return `${v.name} › ${f.name}`
        }
      }
    }
    return game.venue || '-'
  }, [venues, game.surfaceId, game.venue])

  // Format time range for read-only mode
  const timeRangeDisplay = useMemo(() => {
    if (!game.startsAt) {
      return '-'
    }
    const tz = game.timezone || 'UTC'
    const start = dayjs.utc(game.startsAt).tz(tz).format('HH:mm')
    const end = game.endsAt ? dayjs.utc(game.endsAt).tz(tz).format('HH:mm') : ''
    return end ? `${start} - ${end}` : start
  }, [game.startsAt, game.endsAt, game.timezone])

  if (readOnly) {
    return (
      <div css={popoverContainerStyles} onClick={(e) => e.stopPropagation()}>
        <button
          type='button'
          css={closeButtonStyles}
          onClick={(e) => {
            e.stopPropagation()
            onClose()
          }}
          aria-label='Close'
        >
          &times;
        </button>

        <div css={popoverHeaderStyles}>
          <span css={popoverGameLabel}>
            {game.homeTeam} vs {game.awayTeam}
          </span>
        </div>

        <div css={fieldGroupStyles}>
          <label css={fieldLabelStyles}>Date &amp; Time</label>
          <div css={fieldRowStyles}>
            <span css={readOnlyValueStyles}>{selectedDate || '-'}</span>
            <span css={readOnlyValueStyles}>{timeRangeDisplay}</span>
          </div>
        </div>

        <div css={fieldGroupStyles}>
          <label css={fieldLabelStyles}>Venue</label>
          <span css={readOnlyValueStyles}>{venueDisplayName}</span>
        </div>

        <div css={fieldGroupStyles}>
          <div css={teamLabelsRowStyles}>
            <label css={fieldLabelStyles}>Home Team</label>
            <label css={fieldLabelStyles}>Away Team</label>
          </div>
          <div css={fieldRowStyles}>
            <span css={readOnlyValueStyles}>{game.homeTeam}</span>
            <span css={readOnlyValueStyles}>{game.awayTeam}</span>
          </div>
        </div>

        {game.violations && game.violations.length > 0 && (
          <div css={violationsContainerStyles}>
            {game.violations.map((v, i) => (
              <div key={i} css={violationRowStyles}>
                <div css={violationHeaderStyles}>
                  <span
                    css={violationBadgeStyles(
                      v.severity === 'error'
                        ? colors.ERROR_200
                        : v.severity === 'warning'
                        ? colors.WARNING_300
                        : colors.INFO_300
                    )}
                  >
                    {v.severity === 'error'
                      ? 'ERROR'
                      : v.severity === 'warning'
                      ? 'WARNING'
                      : 'INFO'}
                  </span>
                  <span css={violationConstraintNameStyles}>
                    {v.constraintName}
                  </span>
                </div>
                <span css={violationMessageStyles}>{v.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div css={popoverContainerStyles} onClick={(e) => e.stopPropagation()}>
      {/* Close button */}
      <button
        type='button'
        css={closeButtonStyles}
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        aria-label='Close'
      >
        &times;
      </button>

      {/* Game info header */}
      <div css={popoverHeaderStyles}>
        <span css={popoverGameLabel}>
          {game.homeTeam} vs {game.awayTeam}
        </span>
      </div>

      {/* Date & Time */}
      <Global styles={darkDatePickerPopupStyles} />
      <div css={fieldGroupStyles}>
        <label css={fieldLabelStyles}>Date &amp; Time</label>
        <div css={fieldRowStyles}>
          <DatePicker
            css={datePickerFieldStyles}
            value={selectedDate ? dayjs(selectedDate) : null}
            format='YYYY-MM-DD'
            suffixIcon={<CalendarOutlined />}
            allowClear={false}
            popupClassName='calendar-game-edit-datepicker'
            getPopupContainer={(trigger) =>
              (trigger.parentNode as HTMLElement) || document.body
            }
            onChange={(date: Dayjs | null) => {
              if (!date) {
                return
              }
              const newDate = date.format('YYYY-MM-DD')
              const timePart = editForm.startsAt?.slice(11) || '00:00:00'
              setEditForm((prev) => ({
                ...prev,
                startsAt: `${newDate}T${timePart}`,
                endsAt: '',
              }))
            }}
          />
          <select
            css={selectStyles}
            value={selectedTime}
            disabled={!selectedDate || timeSlotOptions.length === 0}
            onChange={(e) => {
              const val = e.target.value
              if (!val) {
                return
              }
              const slot = timeSlotOptions.find((s) => s.start === val)
              if (!slot) {
                return
              }
              const datePart = selectedDate
              setEditForm((prev) => ({
                ...prev,
                startsAt: `${datePart}T${slot.start}:00`,
                endsAt: `${datePart}T${slot.end}:00`,
              }))
            }}
          >
            <option value=''>
              {!selectedDate
                ? 'Pick date first'
                : timeSlotOptions.length === 0
                ? 'No slots'
                : 'Select Time'}
            </option>
            {timeSlotOptions.map((s) => (
              <option key={`${s.start}-${s.end}`} value={s.start}>
                {s.start} - {s.end}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Venue (Location + Surface grouped) */}
      <div css={fieldGroupStyles}>
        <label css={fieldLabelStyles}>Venue</label>
        <select
          css={selectStyles}
          value={venueCompositeValue}
          onChange={(e) => {
            const val = e.target.value
            if (!val) {
              setEditForm((prev) => ({
                ...prev,
                venueId: '',
                facilityId: '',
              }))
              return
            }
            const [newVenueId, newFacilityId] = val.split('::')
            const datePart = editForm.startsAt
              ? editForm.startsAt.slice(0, 10)
              : ''
            setEditForm((prev) => ({
              ...prev,
              venueId: newVenueId,
              facilityId: newFacilityId,
              startsAt: datePart ? `${datePart}T00:00:00` : prev.startsAt,
              endsAt: '',
            }))
          }}
        >
          <option value=''>Select Venue</option>
          {venues.map((v) => (
            <optgroup key={v.id} label={v.name}>
              {(v.facilities ?? []).map((f) => (
                <option key={`${v.id}::${f.id}`} value={`${v.id}::${f.id}`}>
                  {v.name} › {f.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      {/* Home Team + Away Team side-by-side */}
      <div css={fieldGroupStyles}>
        <div css={teamLabelsRowStyles}>
          <label css={fieldLabelStyles}>Home Team</label>
          <label css={fieldLabelStyles}>Away Team</label>
        </div>
        <div css={fieldRowStyles}>
          <select
            css={selectStyles}
            value={editForm.homeTeamId}
            onChange={(e) =>
              setEditForm((prev) => ({
                ...prev,
                homeTeamId: e.target.value,
              }))
            }
          >
            <option value=''>Select Home</option>
            {divisionTeams
              .filter((t) => t.id !== editForm.awayTeamId)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
          <select
            css={selectStyles}
            value={editForm.awayTeamId}
            onChange={(e) =>
              setEditForm((prev) => ({
                ...prev,
                awayTeamId: e.target.value,
              }))
            }
          >
            <option value=''>Select Away</option>
            {divisionTeams
              .filter((t) => t.id !== editForm.homeTeamId)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Constraint violations */}
      {game.violations && game.violations.length > 0 && (
        <div css={violationsContainerStyles}>
          {game.violations.map((v, i) => (
            <div key={i} css={violationRowStyles}>
              <div css={violationHeaderStyles}>
                <span
                  css={violationBadgeStyles(
                    v.severity === 'error'
                      ? colors.ERROR_200
                      : v.severity === 'warning'
                      ? colors.WARNING_300
                      : colors.INFO_300
                  )}
                >
                  {v.severity === 'error'
                    ? 'ERROR'
                    : v.severity === 'warning'
                    ? 'WARNING'
                    : 'INFO'}
                </span>
                <span css={violationConstraintNameStyles}>
                  {v.constraintName}
                </span>
              </div>
              <span css={violationMessageStyles}>{v.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div css={actionButtonsRowStyles}>
        <button
          type='button'
          css={saveButtonStyles}
          onClick={async (e) => {
            e.stopPropagation()
            try {
              await saveChanges()
            } catch (err) {
              console.error('Failed to save game changes:', err)
            }
            onClose()
          }}
        >
          Save
        </button>
        {onMoveToDate && (
          <button
            type='button'
            css={moveToDateButtonStyles}
            onClick={(e) => {
              e.stopPropagation()
              onMoveToDate(game.id)
            }}
          >
            Move to Date
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const popoverContainerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  min-width: 280px;
  max-width: 300px;
  position: relative;
`

const closeButtonStyles = css`
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  color: ${colors.NEUTRAL_70};
  font-size: 18px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;

  &:hover {
    color: ${colors.WHITE};
  }
`

const popoverHeaderStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 20px;
`

const popoverGameLabel = css`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: ${colors.WHITE};
`

const fieldGroupStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const fieldLabelStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const fieldRowStyles = css`
  display: flex;
  gap: 8px;
  align-items: stretch;

  > * {
    flex: 1;
    min-width: 0;
  }
`

const teamLabelsRowStyles = css`
  display: flex;
  gap: 8px;

  > label {
    flex: 1;
  }
`

const readOnlyValueStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${colors.WHITE};
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 6px;
  padding: 6px 8px;
  width: 100%;
`

const selectStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${colors.WHITE};
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 6px;
  padding: 6px 8px;
  width: 100%;
  appearance: none;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: ${colors.PRIMARY_300};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  option {
    background: ${colors.SECONDARY_400};
    color: ${colors.WHITE};
  }
`

const datePickerFieldStyles = css`
  &.ant-picker {
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    color: ${colors.WHITE};
    background: ${colors.SECONDARY_400};
    border: 1px solid ${colors.SECONDARY_200};
    border-radius: 6px;
    padding: 6px 8px;
    width: 100%;
    cursor: pointer;
    outline: none;

    &:hover {
      border-color: ${colors.SECONDARY_200};
    }

    &.ant-picker-focused {
      border-color: ${colors.PRIMARY_300};
      box-shadow: none;
    }

    .ant-picker-input input {
      font-family: 'Inter', sans-serif;
      font-size: 13px;
      color: ${colors.WHITE};

      &::placeholder {
        color: ${colors.NEUTRAL_100};
      }
    }

    .ant-picker-suffix {
      color: ${colors.WHITE};
    }

    .ant-picker-clear {
      background: ${colors.SECONDARY_400};
      color: ${colors.WHITE};
    }
  }
`

const darkDatePickerPopupStyles = css`
  .calendar-game-edit-datepicker {
    .ant-picker-panel-container {
      background-color: ${colors.SECONDARY_300};
      border: 1px solid ${colors.SECONDARY_200};
      border-radius: 8px;
    }

    .ant-picker-panel {
      background: transparent;
      border: none;
    }

    .ant-picker-header {
      color: ${colors.WHITE};
      border-bottom: 1px solid ${colors.SECONDARY_200};

      button {
        color: ${colors.NEUTRAL_50};

        &:hover {
          color: ${colors.WHITE};
        }
      }
    }

    .ant-picker-body {
      th {
        color: ${colors.NEUTRAL_50};
      }
    }

    .ant-picker-content td {
      color: ${colors.WHITE};
    }

    .ant-picker-cell {
      color: ${colors.NEUTRAL_100};

      &:hover:not(.ant-picker-cell-selected) .ant-picker-cell-inner {
        background: ${colors.SECONDARY_200};
      }

      &.ant-picker-cell-in-view {
        color: ${colors.WHITE};
      }

      &.ant-picker-cell-selected .ant-picker-cell-inner {
        background: ${colors.PRIMARY_300};
        color: ${colors.SECONDARY_300};
      }

      &.ant-picker-cell-today .ant-picker-cell-inner::before {
        border-color: ${colors.PRIMARY_300};
      }
    }

    .ant-picker-footer {
      border-top: 1px solid ${colors.SECONDARY_200};

      .ant-picker-today-btn {
        color: ${colors.PRIMARY_300};
      }
    }

    .ant-picker-header-view button {
      color: ${colors.WHITE};

      &:hover {
        color: ${colors.PRIMARY_300};
      }
    }

    /* Year/Month/Decade panel cells */
    .ant-picker-year-panel,
    .ant-picker-month-panel,
    .ant-picker-decade-panel {
      .ant-picker-cell .ant-picker-cell-inner {
        color: ${colors.NEUTRAL_100};
      }

      .ant-picker-cell-in-view .ant-picker-cell-inner {
        color: ${colors.WHITE};
      }

      .ant-picker-cell:hover:not(.ant-picker-cell-selected)
        .ant-picker-cell-inner {
        background: ${colors.SECONDARY_200};
      }

      .ant-picker-cell-selected .ant-picker-cell-inner {
        background: ${colors.PRIMARY_300};
        color: ${colors.SECONDARY_300};
      }
    }
  }
`

const violationsContainerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 2px;
`

const violationRowStyles = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  background: ${colors.SECONDARY_300};
  border-radius: 6px;
`

const violationHeaderStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
`

const violationBadgeStyles = (color: string) => css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${color};
  background: ${color}1A;
  padding: 1px 5px;
  border-radius: 3px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`

const violationConstraintNameStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.NEUTRAL_50};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

const violationMessageStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: ${colors.NEUTRAL_70};
  line-height: 1.3;
`

const actionButtonsRowStyles = css`
  display: flex;
  gap: 8px;
  margin-top: 4px;
`

const saveButtonStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  background: ${colors.PRIMARY_300};
  color: ${colors.SECONDARY_300};
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  flex: 1;

  &:hover {
    background: ${colors.PRIMARY_400};
  }
`

const moveToDateButtonStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  background: transparent;
  color: ${colors.NEUTRAL_50};
  border: 1px solid ${colors.SOFT_STEEL};
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${colors.SECONDARY_300};
    color: ${colors.WHITE};
  }
`

export default CalendarGameEditPopover
